import { FC } from "react";
import {
    Box,
    useBreakpointValue,
    Flex,
    Icon,
    Text,
    Heading,
} from "@smile2impress/components/relaunch";
import {
    RoundBracketLeftIcon,
    WavesPatternIcon,
} from "@smile2impress/components";
import { useGoToBooking } from "hooks/useGoToBooking";

import { TestIds } from "constants/test-ids";
import { CtaButton } from "components/CtaButton";

import { Advantage } from "./components";
import { AdvantagesWithHeadingProps } from "./AdvantagesWithHeading.types";

export const AdvantagesWithHeading: FC<AdvantagesWithHeadingProps> = ({
    content,
    headingAs,
}) => {
    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });
    const { title, subtitle, cta, advantages, disclaimer } = content;

    const { goToBooking } = useGoToBooking();
    if (!cta.onClick && !cta.link) {
        cta.onClick = goToBooking;
    }

    return (
        <Box
            position="relative"
            as="section"
            overflow="hidden"
            bgColor="base.blue"
        >
            <Box
                position="absolute"
                zIndex="0"
                top={["-65px", "-65px", "-400px"]}
                left={["-140px", "-140px", "0px"]}
            >
                {isMobile && (
                    <WavesPatternIcon
                        width="598px"
                        height="640px"
                        gradientColors={[
                            "hsla(230, 76%, 51%, 1)",
                            "hsla(274, 76%, 51%, 1)",
                            "hsla(359, 100%, 66%, 1)",
                        ]}
                    />
                )}
                {!isMobile && (
                    <WavesPatternIcon
                        width="1140px"
                        height="1217px"
                        gradientColors={[
                            "hsla(230, 76%, 51%, 1)",
                            "hsla(274, 76%, 51%, 1)",
                            "hsla(359, 100%, 66%, 1)",
                        ]}
                    />
                )}
            </Box>
            <Flex
                px={[2, 2, 4]}
                pt={[3, 3, 4]}
                pb={[3, 3, disclaimer ? 3 : 4]}
                minHeight={10}
            >
                <Flex
                    zIndex="1"
                    position="relative"
                    direction="column"
                    gap={[5.5, 5.5, 5]}
                    justifyContent="space-between"
                    width="100%"
                >
                    <Flex
                        direction={[
                            "column",
                            "column",
                            subtitle ? "row-reverse" : "row",
                        ]}
                        justifyContent="space-between"
                        width="100%"
                    >
                        {subtitle && (
                            <Flex
                                direction="row"
                                gap="8px"
                                alignSelf="flex-start"
                                alignItems={"center"}
                                mb={[1, 1, 0]}
                            >
                                <Icon
                                    as={RoundBracketLeftIcon}
                                    w="5px"
                                    h="32px"
                                    color={["text.baseInverse"]}
                                />

                                <Text
                                    textAlign="center"
                                    textStyle={["captionSmall"]}
                                    fontWeight="bold"
                                    color={["text.baseInverse"]}
                                >
                                    {subtitle}
                                </Text>

                                <Icon
                                    as={RoundBracketLeftIcon}
                                    w="5px"
                                    h="32px"
                                    color={["text.baseInverse"]}
                                    transform="rotate(180deg)"
                                />
                            </Flex>
                        )}
                        <Flex direction="column" justifyContent="space-between">
                            <Heading
                                textStyle={["H1", "H1", "special"]}
                                color={["text.baseInverse"]}
                                as={headingAs}
                                maxWidth={["100%", "100%", "95%"]}
                                mb={[0, 0, 2]}
                            >
                                {title}
                            </Heading>
                            {!isMobile && (
                                <Box>
                                    <CtaButton
                                        variant="white"
                                        dataTestId={
                                            TestIds.ADVANTAGES_WITH_HEADING_CTA
                                        }
                                        {...cta}
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                    <Flex direction="column">
                        <Flex
                            direction="row"
                            flexWrap={["wrap", "wrap", "nowrap"]}
                            justifyContent={[
                                "flex-start",
                                "flex-start",
                                "space-between",
                            ]}
                            columnGap={[3, 3, "60px"]}
                            rowGap={[2, 2, 0]}
                        >
                            {advantages.map((item, index) => (
                                <Flex key={index}>
                                    <Advantage {...item} />
                                </Flex>
                            ))}
                        </Flex>
                        {disclaimer && (
                            <Box mt={[2, 2, 2]} textAlign="left">
                                <Text
                                    textStyle={["captionSmall"]}
                                    color="text.baseInverse"
                                >
                                    {disclaimer}
                                </Text>
                            </Box>
                        )}
                        {isMobile && (
                            <Box mt={2}>
                                <CtaButton
                                    variant="white"
                                    dataTestId={
                                        TestIds.ADVANTAGES_WITH_HEADING_CTA
                                    }
                                    {...cta}
                                />
                            </Box>
                        )}
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};
